import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { ChevronDown, Plus, SearchLg } from "@untitledui/icons";
import { useFilter } from "react-aria";
import type { Key } from "react-aria-components";
import {
    Autocomplete as AriaAutocomplete,
    Button as AriaButton,
    Dialog as AriaDialog,
    DialogTrigger as AriaDialogTrigger,
    Input as AriaInput,
    ListBox as AriaListBox,
    Popover as AriaPopover,
    SearchField as AriaSearchField,
} from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import { SelectItem } from "./select-item";
import { type CommonProps, SelectContext, type SelectItemType, sizes } from "./select-shared";

const searchSizes = {
    sm: { wrapper: "py-1", root: "px-3 py-2 gap-2 *:data-icon:size-4 *:data-icon:stroke-[2.25px]", text: "text-sm" },
    md: { wrapper: "py-0.5", root: "px-3 py-2 gap-2 *:data-icon:size-5", text: "text-md" },
    lg: { wrapper: "py-0.5", root: "px-3.5 py-2.5 gap-2 *:data-icon:size-5", text: "text-md" },
};

const popoverMaxHeights = {
    sm: "max-h-68",
    md: "max-h-76",
    lg: "max-h-92",
};

interface SearchSelectProps extends CommonProps {
    /** List of selectable items. */
    items?: SelectItemType[];
    /** Render function for each item — typically `<SearchSelect.Item>…</SearchSelect.Item>`. */
    children: ReactNode | ((item: SelectItemType) => ReactNode);
    /** Currently selected key (controlled). */
    selectedKey?: Key | null;
    /** Default selected key (uncontrolled). */
    defaultSelectedKey?: Key | null;
    /** Handler fired when selection changes. */
    onSelectionChange?: (key: Key | null) => void;
    /** Whether the select is disabled. */
    isDisabled?: boolean;
    /** Whether the select is required. */
    isRequired?: boolean;
    /** Whether to show the select in an invalid state. */
    isInvalid?: boolean;
    /** Placeholder text for the trigger when nothing is selected. */
    placeholder?: string;
    /** Search input placeholder inside the popover. */
    searchPlaceholder?: string;
    /** Hide the search input inside the popover. @default false */
    hideSearch?: boolean;
    /** Notified as the user types in the search box. Provide this to drive
     *  **server-side** search: the parent re-queries and feeds fresh
     *  `items`. When set, the built-in client-side label filter is disabled
     *  (the parent owns matching), so server results aren't double-filtered. */
    onSearchChange?: (value: string) => void;
    /** Footer action rendered below the list (e.g. "+ Add New"). */
    actionLabel?: string;
    /** Handler fired when the footer action is clicked. */
    onAction?: () => void;
    /** Custom attribute for the footer action button. */
    actionDataAutotest?: string;
    /** Custom footer node. Overrides actionLabel/onAction when provided. */
    footer?: ReactNode;
    /** Extra classes on the popover. */
    popoverClassName?: string;
    /** Multiplier applied to the trigger width when sizing the popover.
     *  Useful when the trigger is too narrow (e.g. a 2-letter state code)
     *  and the dropdown content needs a bit more breathing room. */
    popoverWidthMul?: number;
    /** Let the selected value in the trigger wrap to multiple lines instead
     *  of truncating with an ellipsis. Useful when a long value must stay
     *  fully readable in a narrow field (e.g. facility names). Default false. */
    wrapValue?: boolean;
    /** Class name for the outer wrapper. */
    className?: string;
    /** Custom attribute for E2E testing */
    "data-autotest"?: string;
    /** Custom attribute for the search input inside the popover. */
    searchDataAutotest?: string;
}

// Shared offscreen canvas for measuring option label widths (used by
// `wrapValue` to size the open popover to its widest option).
let measureCanvas: HTMLCanvasElement | null = null;
function getMeasureCtx(): CanvasRenderingContext2D | null {
    if (typeof document === "undefined") return null;
    if (!measureCanvas) measureCanvas = document.createElement("canvas");
    return measureCanvas.getContext("2d");
}

const SearchSelectRoot = ({
    items,
    children,
    size = "md",
    selectedKey,
    defaultSelectedKey,
    onSelectionChange,
    isDisabled,
    isRequired,
    isInvalid,
    placeholder = "Select",
    searchPlaceholder = "Search",
    hideSearch = false,
    onSearchChange,
    actionLabel,
    onAction,
    actionDataAutotest,
    footer,
    label,
    hint,
    tooltip,
    hideRequiredIndicator,
    popoverClassName,
    popoverWidthMul = 1,
    wrapValue = false,
    className,
    "data-autotest": dataAutotest,
    searchDataAutotest,
}: SearchSelectProps) => {
    const { contains } = useFilter({ sensitivity: "base" });
    const [searchValue, setSearchValue] = useState("");
    // Server-driven search: notify the parent on every keystroke and skip
    // the local label filter (the parent supplies already-matched items).
    const serverMode = !!onSearchChange;
    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchValue(value);
            onSearchChange?.(value);
        },
        [onSearchChange],
    );
    const [internalKey, setInternalKey] = useState<Key | null>(defaultSelectedKey ?? null);
    const currentKey = selectedKey !== undefined ? selectedKey : internalKey;

    const triggerRef = useRef<HTMLButtonElement>(null);
    // The selected-value span — used to detect truncation (scrollWidth >
    // clientWidth) so the tooltip only shows when the name is actually cut off.
    const valueRef = useRef<HTMLSpanElement>(null);
    const [popoverWidth, setPopoverWidth] = useState("");
    // Controlled hover state for the truncated-value tooltip. Driven manually
    // because react-aria's auto TooltipTrigger doesn't fire when nested inside
    // the DialogTrigger button (same controlled pattern as InvalidShell).
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const onResize = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPopoverWidth(rect.width * popoverWidthMul + "px");
    }, [popoverWidthMul]);

    const handleSelectionChange = useCallback(
        (keys: "all" | Set<Key>) => {
            if (keys === "all") return;
            const first = keys.size > 0 ? Array.from(keys)[0] : null;
            setInternalKey(first);
            onSelectionChange?.(first);
        },
        [onSelectionChange],
    );

    const selectedItem = items?.find((item) => item.id === currentKey);
    const hasSelection = !!selectedItem;

    // wrapValue: size the open popover to the WIDEST option (one-line width,
    // clamped to [160, 560]) so long names in the list aren't cramped when the
    // selected value — and therefore the trigger/column — is short. Options
    // still wrap, so anything past 560px breaks onto a second line.
    const hugPopoverWidth = useMemo(() => {
        if (!wrapValue || !items?.length) return undefined;
        const ctx = getMeasureCtx();
        if (!ctx) return undefined;
        ctx.font = "500 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
        let widest = 0;
        for (const it of items) widest = Math.max(widest, ctx.measureText(it.label ?? "").width);
        return Math.max(160, Math.min(Math.ceil(widest) + 56, 560));
    }, [wrapValue, items]);

    const selectedKeysSet = currentKey != null ? new Set<Key>([currentKey]) : new Set<Key>();

    /** Manual mirror of AriaAutocomplete's internal filter so the parent
     *  knows whether the listbox will render any items. We use this to
     *  swap in the no-results empty state and hide the bottom pill
     *  footer (the empty state has its own action affordance). */
    const filteredCount = (() => {
        if (!items) return 0;
        // Server mode: the parent already filtered — empty state only when
        // the server returned nothing.
        if (serverMode) return items.length;
        if (searchValue.trim() === "") return items.length;
        return items.filter((item) => {
            const label = typeof item.label === "string" ? item.label : "";
            return contains(label, searchValue);
        }).length;
    })();
    const isEmptyResults = filteredCount === 0;

    /** Empty-state UI rendered inside the listbox area when search
     *  filters every item out. Centred icon + title + helper text + a
     *  link-style Add New that mirrors the bottom pill button's onAction. */
    const renderEmptyState = (close: () => void) => (
        <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
            <div className="flex size-10 items-center justify-center rounded-lg bg-primary ring-1 ring-secondary">
                <SearchLg aria-hidden="true" className="size-5 text-fg-quaternary" />
            </div>
            <div className="flex flex-col gap-1">
                <p className="text-sm font-semibold text-primary">No Results Found</p>
                <p className="text-sm text-tertiary">Please try a different search term.</p>
            </div>
            {actionLabel && (
                <button
                    type="button"
                    onClick={() => {
                        close();
                        onAction?.();
                    }}
                    data-autotest={actionDataAutotest}
                    className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-semibold text-brand-secondary transition duration-100 ease-linear hover:text-brand-secondary_hover"
                >
                    <Plus aria-hidden="true" className="size-4 stroke-[2.25px]" />
                    {actionLabel}
                </button>
            )}
        </div>
    );

    const renderFooter = (close: () => void) => {
        if (footer) return footer;
        if (!actionLabel) return null;
        // Pill-style outlined button anchored to the bottom of the dropdown.
        // Hidden when the listbox is in its empty state — the empty state
        // already exposes Add New as a centred link, so we'd otherwise
        // double up on the affordance.
        if (isEmptyResults) return null;
        return (
            <div className="border-t border-secondary p-3">
                <button
                    type="button"
                    onClick={() => {
                        close();
                        onAction?.();
                    }}
                    data-autotest={actionDataAutotest}
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-sm font-semibold text-primary ring-1 ring-secondary transition duration-100 ease-linear hover:bg-secondary"
                >
                    <Plus aria-hidden="true" className="size-4 stroke-[2.25px]" />
                    {actionLabel}
                </button>
            </div>
        );
    };

    return (
        <SelectContext.Provider value={{ size }}>
            <div className={cx("flex flex-col gap-1.5", className)}>
                {label && (
                    <Label isRequired={hideRequiredIndicator ? false : isRequired} isInvalid={isInvalid} tooltip={tooltip}>
                        {label}
                    </Label>
                )}

                <AriaDialogTrigger>
                    {/* Centered dark tooltip with the full name, shown on hover only when the
                        value is actually truncated. Controlled so it fires on the DialogTrigger
                        button (react-aria's auto TooltipTrigger won't when nested here). */}
                    <Tooltip
                        title={hasSelection ? String(selectedItem.label) : ""}
                        isOpen={tooltipOpen}
                        // Top-right (near the ellipsis/chevron), slightly overlapping the input's
                        // top border, no arrow, centered + a touch wider (Andressa's design).
                        placement="top end"
                        arrow={false}
                        offset={-4}
                        contentClassName="max-w-md items-center text-center"
                    >
                    <AriaButton
                        ref={triggerRef}
                        isDisabled={isDisabled}
                        onClick={onResize}
                        data-autotest={dataAutotest}
                        // wrapValue: show the tooltip on hover only when the value overflows its
                        // single line. The field itself fills its cell (uniform column width).
                        onHoverStart={() => {
                            const el = valueRef.current;
                            setTooltipOpen(!!wrapValue && !!el && el.scrollWidth > el.clientWidth);
                        }}
                        onHoverEnd={() => setTooltipOpen(false)}
                        className={(state) =>
                            cx(
                                "relative flex w-full cursor-pointer items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset",
                                (state.isFocusVisible || state.isPressed) && "ring-2 ring-brand",
                                state.isDisabled && "cursor-not-allowed opacity-50",
                                isInvalid && "ring-error_subtle",
                            )
                        }
                    >
                        <span
                            className={cx(
                                "flex w-full items-center truncate text-left",
                                sizes[size].root,
                                "*:data-icon:shrink-0 *:data-icon:text-fg-quaternary",
                            )}
                        >
                            {hasSelection ? (
                                <span ref={valueRef} className={cx("min-w-0 flex-1 truncate font-medium text-primary", sizes[size].text)}>
                                    {selectedItem.label}
                                </span>
                            ) : (
                                <span className={cx("text-placeholder", sizes[size].text)}>{placeholder}</span>
                            )}

                            <ChevronDown
                                aria-hidden="true"
                                className={cx("ml-auto shrink-0 text-fg-quaternary", size === "lg" ? "size-5" : "size-4 stroke-[2.25px]")}
                            />
                        </span>
                    </AriaButton>
                    </Tooltip>

                    <AriaPopover
                        placement="bottom"
                        offset={4}
                        containerPadding={0}
                        style={{ width: (wrapValue ? hugPopoverWidth : popoverWidth) || undefined }}
                        className={(state) =>
                            cx(
                                "w-(--trigger-width) origin-(--trigger-anchor-point) overflow-hidden rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt outline-hidden will-change-transform",
                                state.isEntering &&
                                    "duration-150 ease-out animate-in fade-in placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5",
                                state.isExiting &&
                                    "duration-100 ease-in animate-out fade-out placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5",
                                popoverClassName,
                            )
                        }
                    >
                        <AriaDialog className="outline-hidden">
                            {({ close }) => {
                                // Single-select dropdowns should dismiss as soon as a value
                                // lands, matching the native <select> behavior. The dialog's
                                // `close` is only available inside this render prop, so we
                                // build the listbox handler here to capture it.
                                const onListBoxSelectionChange = (keys: "all" | Set<Key>) => {
                                    handleSelectionChange(keys);
                                    if (keys !== "all" && keys.size > 0) close();
                                };
                                return (
                                    <>
                                        <AriaAutocomplete
                                            filter={serverMode ? undefined : contains}
                                            inputValue={searchValue}
                                            onInputChange={handleSearchChange}
                                        >
                                            {!hideSearch && (
                                                <div className={cx("border-b border-secondary", searchSizes[size].wrapper)}>
                                                    <AriaSearchField aria-label="Search" value={searchValue} onChange={handleSearchChange} autoFocus>
                                                        <div className={cx("flex items-center", searchSizes[size].root)}>
                                                            <SearchLg data-icon aria-hidden="true" className="shrink-0 text-fg-quaternary" />
                                                            <AriaInput
                                                                placeholder={searchPlaceholder}
                                                                data-autotest={searchDataAutotest}
                                                                className={cx(
                                                                    "w-full appearance-none bg-transparent text-primary caret-alpha-black/90 outline-hidden placeholder:text-placeholder",
                                                                    searchSizes[size].text,
                                                                )}
                                                            />
                                                        </div>
                                                    </AriaSearchField>
                                                </div>
                                            )}

                                            <AriaListBox
                                                aria-label={label || "Options"}
                                                items={items}
                                                selectionMode="single"
                                                selectedKeys={selectedKeysSet}
                                                onSelectionChange={onListBoxSelectionChange}
                                                renderEmptyState={() => renderEmptyState(close)}
                                                className={cx("overflow-y-auto py-1 outline-hidden", popoverMaxHeights[size])}
                                            >
                                                {children}
                                            </AriaListBox>
                                        </AriaAutocomplete>

                                        {renderFooter(close)}
                                    </>
                                );
                            }}
                        </AriaDialog>
                    </AriaPopover>
                </AriaDialogTrigger>

                {hint && (
                    <HintText isInvalid={isInvalid} className={cx(size === "sm" && "text-xs")}>
                        {hint}
                    </HintText>
                )}
            </div>
        </SelectContext.Provider>
    );
};

const SearchSelect = SearchSelectRoot as typeof SearchSelectRoot & {
    Item: typeof SelectItem;
};

SearchSelect.Item = SelectItem;

export { SearchSelect };
